import { getDialogueForBeat } from './dialogue.js';

export const CREDITS = { story: 'Story by Miles Peart' };

// Keep the four opening cards separate from the playable campaign contract.
export const STORY_CHAPTERS = [
  {
    id: 'before-the-fall',
    eyebrow: 'Ten years ago',
    title: 'Before the clocks stopped',
    summary:
      `Kronopolis was green, open, and free. Its blacksmiths forged the Time Gauntlet from meranium to defend the elected king and queen's city. ${CREDITS.story}.`,
    image: '/kronopolis_exterior.png',
    tone: 'dawn',
    musicCue: 'fall',
  },
  {
    id: 'kronos-rises',
    eyebrow: 'The invasion',
    title: 'Kronos took the city',
    summary:
      'The warlord Kronos came for the gauntlet. The city fought. The king fell. Millions lost their lives—and Kronopolis became a fortress.',
    image: '/kronos.png',
    tone: 'siege',
    musicCue: 'kronos',
  },
  {
    id: 'mikey-survives',
    eyebrow: 'The thief',
    title: 'Mikey learned to disappear',
    summary:
      'Mikey Zillman lost his parents in the invasion. Raised by a crew of pickpockets, he spent ten years learning every alley, rooftop, and weakness in Kronos’s walls.',
    image: '/mikey_zillman.png',
    tone: 'night',
    musicCue: 'pickpocket',
  },
  {
    id: 'the-plan',
    eyebrow: 'Tonight',
    title: 'Steal back time',
    summary:
      'Sixteen-year-old Mikey has mapped the fortress and its traps. His crew will leave a bomb at the front entrance while he slips through the vents at the back. The plan can’t fail. Probably.',
    image: '/time_gauntlet.png',
    tone: 'gauntlet',
    musicCue: 'plan',
  },
];

export const MISSION_OBJECTIVES = [
  {
    id: 'cross-rooftops',
    label: 'Reach the old clock bridge',
    detail: 'Lift the practice wrench from the crew training dummy, then cross the old clock bridge.',
    zone: { min: [-6.4, 0, 3], max: [6.4, 4, 8] },
    requires: 'wrench',
    interactables: [{ id: 'wrench', kind: 'pickpocket', position: [0, 1.5, 12], verb: 'Lift practice wrench', grants: ['practice-wrench'], result: 'Practice wrench pocketed. Cross the bridge.' }],
    threshold: 8,
    trigger: { type: 'z-threshold', threshold: 8 },
  },
  {
    id: 'pass-checkpoint',
    label: 'Slip past the searchlights',
    detail: 'Unbolt the service grate with the practice wrench. Cross between the lights.',
    zone: { min: [-6.4, 0, -17], max: [6.4, 4, -12] },
    requires: 'vent',
    interactables: [{ id: 'vent', kind: 'vent', position: [0, 1.5, -8], verb: 'Unbolt service grate', requires: 'practice-wrench', result: 'The service grate comes loose.' }],
    threshold: -12,
    trigger: { type: 'z-threshold', threshold: -12 },
  },
  {
    id: 'reach-fortress',
    label: 'Reach the fortress gate',
    detail: 'Lower the access ladder and reach the fortress approach. The crew takes the front; you take the back.',
    zone: { min: [-6.4, 0, -36], max: [6.4, 4, -31] },
    requires: 'ladder',
    interactables: [{ id: 'ladder', kind: 'ladder', position: [0, 1.5, -27], verb: 'Lower ladder', requires: 'practice-wrench', result: 'The fortress approach is open.' }],
    threshold: -31,
    trigger: { type: 'z-threshold', threshold: -31 },
  },
];

// Presentation palettes retained for the prototype, not additional gauntlet powers.
export const ERAS = [
  { id: 'present', label: 'Present', accent: '#68f7d4' },
  { id: 'echo', label: 'Memory echo', accent: '#ffd27a' },
  { id: 'fracture', label: 'Time fracture', accent: '#d68cff' },
];

const event = (eventId) => ({ type: 'event', eventId });
const zone = (zoneId) => ({ type: 'zone', zoneId });

function beat(id, label, detail, trigger) {
  return {
    id,
    objective: { id: `${id}:objective`, label, detail, trigger },
    comms: getDialogueForBeat(id),
  };
}

function chapter({ inventory = [], ...definition }) {
  return {
    availability: 'planned',
    ...definition,
    checkpoint: {
      id: `${definition.id}:start`,
      chapterId: definition.id,
      beatId: definition.beats[0].id,
      spawnId: `${definition.id}:entry`,
      inventory,
      sceneState: definition.id,
    },
  };
}

export const STORY_ACTS = [
  { id: 'a-city-worth-stealing', title: 'A city worth stealing' },
  { id: 'the-plan-cant-fail', title: 'The plan can’t fail' },
  { id: 'closer-this-time', title: 'Closer this time' },
  { id: 'steal-back-time', title: 'Steal back time' },
];

// Ordered campaign design. Only rooftop-route has a playable route in the app.
export const STORY_MODE_CHAPTERS = [
  chapter({
    id: 'fall-of-kronopolis', actId: 'a-city-worth-stealing', title: 'The smell of apples',
    goal: 'Follow the old market lane through Mikey’s memory of the fall.',
    sessionMinutes: [2, 3],
    beats: [
      beat('green-kingdom', 'Follow the harvest carts', 'Walk through the green city and its elected rulers’ square.', zone('harvest-square')),
      beat('meranium-forge', 'Pass the blacksmiths', 'See the meranium gauntlet made to defend Kronopolis.', zone('forge-window')),
      beat('city-falls', 'Reach shelter', 'The invasion takes the king, millions of lives, and Mikey’s parents.', zone('market-shelter')),
    ],
  }),
  chapter({
    id: 'raised-by-pickpockets', actId: 'a-city-worth-stealing', title: 'Apples and watches',
    goal: 'Learn the trade with the people who take you in.', sessionMinutes: [2, 4],
    beats: [
      beat('food-and-family', 'Reach for food', 'The pickpockets find hungry Mikey trying to steal food and take him in.', event('food-attempt')),
      beat('learn-the-lift', 'Lift a practice watch', 'Learn a careful reach and clean withdrawal with the crew.', event('practice-watch-lifted')),
      beat('ten-years-later', 'Carry the apples home', 'Ten years pass; at sixteen, you can steal bushels and golden watches.', zone('crew-table')),
    ],
  }),
  chapter({
    id: 'fortress-plan', actId: 'the-plan-cant-fail', title: 'Classified reasons',
    goal: 'Trace the fortress plan and meet Frank at the exit.', sessionMinutes: [2, 3],
    beats: [
      beat('map-the-traps', 'Trace the back route', 'Follow Mikey’s mapped traps to the rear vents.', event('route-traced')),
      beat('bomb-and-vents', 'Mark the two entrances', 'The crew takes the bomb to the front; you use the vents at the back.', event('entrances-marked')),
      beat('franks-call', 'Join Frank', 'Leave together when Frank calls you.', zone('planning-exit')),
    ],
  }),
  chapter({
    id: 'rooftop-route', actId: 'the-plan-cant-fail', title: 'Above the patrols',
    availability: 'prototype-route',
    musicCue: 'stealth',
    outroCue: 'plan',
    goal: 'Reach the fortress approach for the first heist.', sessionMinutes: [2, 4],
    beats: MISSION_OBJECTIVES.map((objective, index) => ({
      id: ['clock-bridge', 'searchlight-crossing', 'fortress-approach'][index],
      objective,
      comms: getDialogueForBeat(['clock-bridge', 'searchlight-crossing', 'fortress-approach'][index]),
    })),
  }),
  chapter({
    id: 'first-heist', actId: 'the-plan-cant-fail', title: 'One second to run',
    goal: 'Infiltrate the back vents, then escape when Kronos releases time.', sessionMinutes: [3, 5],
    beats: [
      beat('front-bomb', 'Wait for the front signal', 'The crew leaves the bomb at the entrance as you reach the back.', event('front-bomb-placed')),
      beat('back-vents', 'Crawl toward the gauntlet', 'Enter through the rear vents and get within reach.', zone('first-vault-overlook')),
      beat('frozen-crew', 'Find your friends', 'Kronos freezes you with the gauntlet; your friends are chained and frozen behind him.', event('kronos-unfreezes-mikey')),
      beat('unfrozen-flight', 'Run before the chains close', 'Kronos unfreezes you to chain you; escape as he orders pursuit.', zone('heist-escape')),
    ],
  }),
  chapter({
    id: 'month-on-the-run', actId: 'the-plan-cant-fail', title: 'A face in the market',
    goal: 'Steal food while searching the crowd for your friends.', sessionMinutes: [2, 4],
    beats: [
      beat('month-of-hiding', 'Cross the hiding places', 'A month passes between stolen meals and empty sleeping places.', zone('month-market')),
      beat('familiar-faces', 'Take food and look up', 'You pause because you think you see your friends.', event('friends-mistaken')),
      beat('recaptured', 'Try to escape the guards', 'The guards recognize you and catch you while you are still surprised.', event('mikey-recaptured')),
    ],
  }),
  chapter({
    id: 'lose-on-purpose', actId: 'closer-this-time', title: 'A better way in',
    goal: 'Offer the fight, dodge Kronos, and deliberately lose to reach the dungeon.', sessionMinutes: [3, 4],
    beats: [
      beat('combat-bargain', 'Propose hand-to-hand combat', 'If Kronos wins, the dungeon; if you win, freedom. First knockout wins.', event('bargain-accepted')),
      beat('dodge-kronos', 'Dodge his blows', 'Kronos hits hard and fast, but you can avoid him.', event('kronos-dodged')),
      beat('deliberate-loss', 'Let him land the blow', 'Choose to lose so you can reach the gauntlet and your friends from inside.', event('loss-chosen')),
      beat('cell-arrival', 'Step into the cell', 'The guard throws you in. You could have walked.', zone('dungeon-cell')),
    ],
  }),
  chapter({
    id: 'stolen-wrench', actId: 'closer-this-time', title: 'Clogged toilet, open door',
    goal: 'Pickpocket the repairer and unbolt the cell vent.', sessionMinutes: [3, 5],
    beats: [
      beat('toilet-repair', 'Lift the wrench', 'Take the wrench from the person fixing a clogged toilet.', event('wrench-stolen')),
      beat('tight-screws', 'Loosen the tight screws', 'Work each fastener until the vent cover comes free.', event('vent-unbolted')),
      beat('cell-vent', 'Climb into the vent', 'Remove the cover and crawl out of the cell.', zone('cell-duct')),
    ],
  }),
  chapter({
    id: 'steal-the-gauntlet', actId: 'closer-this-time', title: 'The inside of the lock',
    goal: 'Look through the vent cracks and take the gauntlet from inside the defenses.',
    sessionMinutes: [3, 5], inventory: ['wrench'],
    beats: [
      beat('vent-cracks', 'Find the gauntlet below', 'Peek through cracks until you find the guarded chamber.', zone('gauntlet-crack')),
      beat('inside-the-defenses', 'Drop inside the perimeter', 'The guards and lasers are outside; the vent puts you inside them.', zone('gauntlet-pedestal')),
      beat('gauntlet-rush', 'Put on the gauntlet', 'Feel the rush of power when you wear it.', event('gauntlet-worn')),
      beat('pocket-the-gauntlet', 'Remove and pocket it', 'The gauntlet collapses when removed. Climb back into the vent.', event('gauntlet-pocketed')),
    ],
  }),
  chapter({
    id: 'find-the-crew', actId: 'closer-this-time', title: 'Three familiar voices',
    goal: 'Find Frank, Brian, and Jack, then bring them into the vent.',
    sessionMinutes: [2, 4], inventory: ['wrench', 'gauntlet'],
    beats: [
      beat('crew-below', 'Break through to your friends', 'Find the crew, break the vent, and drop into their room.', event('crew-vent-broken')),
      beat('crew-reunion', 'Show them you made it', 'They are skinny and scared, then relieved. Listen as you move toward the exit.', event('crew-ready')),
      beat('crew-into-vent', 'Lead everyone up', 'Tell them you have the gauntlet; all four climb into the cramped duct.', zone('crew-duct')),
    ],
  }),
  chapter({
    id: 'kronos-room', actId: 'steal-back-time', title: 'Wrong room',
    goal: 'Crawl with the crew, survive the fall, and run when the alarm sounds.',
    sessionMinutes: [2, 4], inventory: ['wrench', 'gauntlet'],
    beats: [
      beat('cramped-crawl', 'Keep the crew moving', 'The vent is cramped with all four of you inside.', zone('weak-duct')),
      beat('bedroom-fall', 'Land quietly', 'The duct gives way into Kronos’s room; you try to stay quiet.', event('bedroom-entered')),
      beat('prison-alarm', 'Run for the door', 'The escape alarm wakes Kronos. He sees you and you run.', event('alarm-raised')),
    ],
  }),
  chapter({
    id: 'escape-outside', actId: 'steal-back-time', title: 'Air, at last',
    goal: 'Get everyone outside and remember the bomb.', sessionMinutes: [2, 3],
    inventory: ['wrench', 'gauntlet'],
    beats: [
      beat('outside-together', 'Reach open air', 'Escape the fortress with Frank, Brian, and Jack.', zone('outside-rally')),
      beat('rewind-idea', 'Reach for the gauntlet', 'The bomb was defused. You could go back and bring it with you.', event('rewind-chosen')),
    ],
  }),
  chapter({
    id: 'ten-minutes-earlier', actId: 'steal-back-time', title: 'Steal the same thing twice',
    goal: 'Rewind about ten minutes, wait through the capture, and retrieve the bomb.',
    sessionMinutes: [3, 5], inventory: ['wrench', 'gauntlet'],
    beats: [
      beat('rewind-ten-minutes', 'Go back about ten minutes', 'Use the gauntlet to enter the earlier encounter described in the story.', event('rewind-arrived')),
      beat('wait-for-capture', 'Wait out of sight', 'Watch the fight unfold and wait until Kronos catches them.', event('earlier-capture-complete')),
      beat('retrieve-bomb', 'Sneak in and take the bomb', 'Retrieve the bomb to bring with you, as Mikey describes.', event('bomb-retrieved')),
    ],
  }),
  chapter({
    id: 'the-bomb', actId: 'steal-back-time', title: 'The city breathes',
    goal: 'Set up the bomb with the crew, run, and live to see Kronopolis free.',
    sessionMinutes: [2, 4], inventory: ['wrench', 'gauntlet', 'bomb'],
    beats: [
      beat('set-the-bomb', 'Set the bomb with the crew', 'Place the story prop at the fortress; the crew is together again.', event('final-bomb-placed')),
      beat('run-from-fortress', 'Run with your friends', 'Reach cover together before the explosion.', zone('final-cover')),
      beat('fortress-destroyed', 'Watch from safety', 'The explosion destroys Kronos and his fortress.', event('fortress-destroyed')),
      beat('happily-ever-after', 'Walk home together', 'You and your friends live happily ever after. The end.', zone('homecoming')),
    ],
  }),
];

export const STORY_BEATS = STORY_MODE_CHAPTERS.flatMap((entry) =>
  entry.beats.map((entryBeat) => ({ ...entryBeat, chapterId: entry.id })),
);
export const STORY_OBJECTIVES = STORY_BEATS.map((entry) => ({
  ...entry.objective, beatId: entry.id, chapterId: entry.chapterId,
}));
export const CHECKPOINTS = STORY_MODE_CHAPTERS.map((entry) => entry.checkpoint);

/** Evaluate one trigger. Missing geometry or malformed input never completes it. */
export function isTriggerSatisfied(trigger, context = {}) {
  if (!trigger || !context) return false;
  if (trigger.type === 'event') {
    return typeof trigger.eventId === 'string' && context.eventId === trigger.eventId;
  }
  if (trigger.type === 'z-threshold') {
    const z = context.position?.z;
    return Number.isFinite(z) && Number.isFinite(trigger.threshold) && z <= trigger.threshold;
  }
  if (trigger.type === 'zone') {
    const bounds = context.zones?.[trigger.zoneId];
    return ['x', 'y', 'z'].every((axis) => {
      const value = context.position?.[axis];
      const min = bounds?.min?.[axis];
      const max = bounds?.max?.[axis];
      return Number.isFinite(value) && Number.isFinite(min) && Number.isFinite(max)
        && min <= max && value >= min && value <= max;
    });
  }
  return false;
}

/** Advance at most one objective; numeric z and bare thresholds stay supported. */
export function getNextObjectiveIndex(currentIndex, playerState, objectives = MISSION_OBJECTIVES) {
  if (!Number.isInteger(currentIndex) || currentIndex < 0) return currentIndex;
  const objective = objectives[currentIndex];
  if (!objective) return currentIndex;
  const context = typeof playerState === 'number' ? { position: { z: playerState } } : playerState;
  const trigger = objective.trigger ?? { type: 'z-threshold', threshold: objective.threshold };
  return isTriggerSatisfied(trigger, context) ? currentIndex + 1 : currentIndex;
}
